/**
 * 스와이프 뒤로가기 밑에 깔 "이전 화면" 복사본 보관소와 위치 계산 — DOM 을 모르는 순수 코드(단위 테스트: tests/swipeBackSnapshot.test.ts).
 *
 * 저장 단위는 히스토리 항목 하나(react-router 가 history.state.key 에 적는 값)다.
 * 화면을 떠날 때(pushState 직전) 그 화면의 본문을 복사해 두고, "새 항목 → 떠난 항목" 연결을 함께 적는다.
 * 스와이프할 때는 지금 항목에 연결된 이전 항목의 복사본을 꺼낸다.
 */

/** 메모리에 두는 복사본 수 — 홈처럼 노드가 많은 화면도 있어 최근 것만 둔다 */
export const SNAPSHOT_LIMIT: number = 5;
/** 이전 항목 연결은 가벼우니 넉넉히 두되 끝없이 늘지 않게 자른다 */
export const BACK_LINK_LIMIT: number = 100;
/**
 * 끄는 동안 현재 화면 왼쪽 가장자리 그림자 — 이전 화면 복사본이 없을 때(빈 배경)만 쓴다.
 * 복사본이 있으면 아이폰처럼 겹쳐 놓인 느낌의 그림자(underlayEdgeShadow)를 쓴다.
 */
export const EDGE_SHADOW: string = '-3px 0 10px rgba(31, 27, 22, 0.08)';

export interface SnapshotEntry<T> {
    /** 복사한 화면의 히스토리 항목 key */
    key: string;
    /** 복사본(본문 DOM) */
    node: T;
    /** 복사할 때의 window.scrollY */
    scrollY: number;
    /** 복사할 때 본문 첫 요소의 문서 기준 위쪽 위치(rect.top + scrollY) */
    contentTop: number;
    /** 복사할 때의 뷰포트 폭 — 폭이 바뀌면(회전 등) 배치가 달라 쓰지 않는다 */
    width: number;
}

export class SnapshotStore<T> {
    private readonly limit: number;
    private entries: SnapshotEntry<T>[] = [];
    private readonly backLinks: Map<string, string> = new Map<string, string>();

    constructor(limit: number = SNAPSHOT_LIMIT) {
        this.limit = Math.max(1, limit);
    }

    /** 복사본을 넣는다 — 같은 key 는 새 것으로 바꾸고, 한도를 넘으면 가장 오래된 것부터 버린다 */
    save(entry: SnapshotEntry<T>): void {
        this.entries = this.entries.filter((item: SnapshotEntry<T>) => item.key !== entry.key);
        this.entries.push(entry);
        while (this.entries.length > this.limit) this.entries.shift();
    }

    /** nextKey 항목에서 뒤로 가면 prevKey 항목이 나온다 */
    link(nextKey: string, prevKey: string): void {
        this.backLinks.delete(nextKey);
        this.backLinks.set(nextKey, prevKey);
        while (this.backLinks.size > BACK_LINK_LIMIT) {
            const oldest: string | undefined = this.backLinks.keys().next().value;
            if (oldest === undefined) break;
            this.backLinks.delete(oldest);
        }
    }

    /** replaceState 로 지금 항목의 key 가 바뀌면 이전 항목 연결을 새 key 로 옮긴다 */
    relink(oldKey: string, newKey: string): void {
        if (oldKey === newKey) return;
        const prevKey: string | undefined = this.backLinks.get(oldKey);
        if (prevKey === undefined) return;
        this.backLinks.delete(oldKey);
        this.link(newKey, prevKey);
    }

    /** 지금 항목 바로 앞 항목의 복사본 — 없거나 뷰포트 폭이 달라졌으면 null(빈 배경으로 둔다) */
    previousOf(currentKey: string, width: number): SnapshotEntry<T> | null {
        const prevKey: string | undefined = this.backLinks.get(currentKey);
        if (prevKey === undefined) return null;
        const entry: SnapshotEntry<T> | undefined = this.entries.find((item: SnapshotEntry<T>) => item.key === prevKey);
        if (!entry || entry.width !== width) return null;
        return entry;
    }

    /** 보관 중인 복사본 key(오래된 것부터) — 테스트·점검용 */
    keys(): string[] {
        return this.entries.map((item: SnapshotEntry<T>) => item.key);
    }
}

/** 이전 화면이 시작하는 왼쪽 위치(화면 폭 대비) — 아이폰 기본 뒤로가기(interactive pop)를 초당 30장으로 잰 값(시작 -25~30%, 중간 약 -11%) */
export const UNDERLAY_START_RATIO: number = 0.3;
/** 현재 화면 가장자리 그림자 진하기 — 시작 값, 끝까지 끌면 그 절반까지 옅어진다 */
export const UNDERLAY_SHADOW_ALPHA: number = 0.12;

const progressOf = (value: number, width: number): number => (width > 0 ? Math.max(0, Math.min(1, value / width)) : 1);

/**
 * 현재 화면을 value 만큼 끌었을 때 이전 화면 복사본의 가로 위치(px) — 아이폰처럼 앞뒤 속도가 다르다.
 * 현재 화면은 손가락을 그대로(+value), 이전 화면은 -0.3·폭·(1-p) 로 느리게 따라온다(p = value/폭). p=0 → -0.3폭, 0.5 → -0.15폭, 1 → 0.
 */
export const underlayOffsetX = (value: number, width: number): number => {
    const offset: number = -UNDERLAY_START_RATIO * width * (1 - progressOf(value, width));
    return offset === 0 ? 0 : offset;
};

/** 이전 화면 위에 겹친 현재 화면의 왼쪽 가장자리 그림자 — 진행할수록 조금씩 옅어진다(0.12 → 0.06). 제자리면 없음 */
export const underlayEdgeShadow = (value: number, width: number): string => {
    if (value <= 0) return '';
    const alpha: number = UNDERLAY_SHADOW_ALPHA * (1 - 0.5 * progressOf(value, width));
    return `-6px 0 16px rgba(0, 0, 0, ${Math.round(alpha * 1000) / 1000})`;
};

/** 현재 화면을 value 만큼 끌었을 때의 가장자리 그림자 — 제자리(0 이하)면 없음 */
export const edgeShadowOf = (value: number): string => (value > 0 ? EDGE_SHADOW : '');

/**
 * 고정 레이어 안에서 복사본을 얼마나 위로 올릴지(px, 음수면 위로).
 * 복사본이 저장할 때 화면에 보이던 그 자리에 오도록 저장한 scrollY 만큼 올리고, 레이어가 뷰포트 맨 위가 아닌 곳(layerTop)에서 시작하면 그만큼 뺀다.
 */
export const snapshotTopOffset = (contentTop: number, scrollY: number, layerTop: number): number =>
    contentTop - scrollY - layerTop;

/** history.state 에서 react-router 항목 key 를 꺼낸다 — 첫 화면처럼 key 가 없으면 react-router 와 같은 'default' */
export const historyKeyOf = (state: unknown): string => {
    const key: unknown = (state as { key?: unknown } | null)?.key;
    return typeof key === 'string' && key !== '' ? key : 'default';
};

/** pushState/replaceState 에 넘어온 새 state 의 key — react-router 이동이 아니면(key 없음) null */
export const nextHistoryKeyOf = (state: unknown): string | null => {
    const key: unknown = (state as { key?: unknown } | null)?.key;
    return typeof key === 'string' && key !== '' ? key : null;
};
